import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Gesture as SignatureIcon,
  Badge as InitialsIcon,
  CalendarToday as DateIcon,
  TextFields as TextIcon,
  CheckBoxOutlined as CheckboxIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { portant } from '../../theme/portantTheme';
import { copyImageHtml } from '../../taskpane';

const STATIC_BASE = 'https://static.portant.co';
// Portant only ships detectable placeholder digests for recipients 1-4.
const MAX_RECIPIENTS = 4;

interface FieldDef {
  key: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
  supportsRequired: boolean;
}

// Order and required-support mirror the backend DIGEST_FIELD_MAP: signature and
// date have no optional state (a signature is always required, a date is auto
// stamped), and only text and checkbox ship a detectable -required variant.
const FIELDS: FieldDef[] = [
  { key: 'signature', label: 'Signature', hint: 'Sign here', icon: <SignatureIcon fontSize="small" />, supportsRequired: false },
  { key: 'date', label: 'Date', hint: 'Auto-stamped on signing', icon: <DateIcon fontSize="small" />, supportsRequired: false },
  { key: 'initials', label: 'Initials', hint: 'Initial here', icon: <InitialsIcon fontSize="small" />, supportsRequired: false },
  { key: 'text', label: 'Text box', hint: 'Signer fills in', icon: <TextIcon fontSize="small" />, supportsRequired: true },
  { key: 'checkbox', label: 'Checkbox', hint: 'Signer ticks', icon: <CheckboxIcon fontSize="small" />, supportsRequired: true },
];

interface Props {
  onNotice: (message: string) => void;
}

const copyKey = (recipient: number, fieldKey: string, req: boolean) =>
  `${recipient}-${fieldKey}-${req ? 'req' : 'std'}`;

const SignatureFields: React.FC<Props> = ({ onNotice }) => {
  const [count, setCount] = useState(1);
  const [expanded, setExpanded] = useState(1);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const fieldUrl = (recipient: number, field: FieldDef, req: boolean) => {
    const suffix = field.supportsRequired && req ? '-required' : '';
    return `${STATIC_BASE}/field-${field.key}${suffix}-u${recipient}.png`;
  };

  const handleCopy = async (recipient: number, field: FieldDef, req: boolean) => {
    const ok = await copyImageHtml(fieldUrl(recipient, field, req));
    if (ok) {
      setCopiedKey(copyKey(recipient, field.key, req));
      setTimeout(() => setCopiedKey(null), 1500);
      onNotice('Field copied. Paste it into the document where the signer should complete it.');
    } else {
      onNotice("Couldn't copy the field. Please try again.");
    }
  };

  const addRecipient = () => {
    if (count >= MAX_RECIPIENTS) return;
    const next = count + 1;
    setCount(next);
    setExpanded(next);
  };

  const removeLastRecipient = () => {
    if (count <= 1) return;
    if (expanded === count) setExpanded(count - 1);
    setCount(count - 1);
  };

  return (
    <Box sx={{ pb: 2 }}>
      <Box
        sx={{
          m: 2,
          mb: 1.5,
          p: 1.75,
          border: `1px solid ${portant.parchment}`,
          borderRadius: 2,
          bgcolor: portant.paper,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <InfoIcon fontSize="small" sx={{ color: portant.blue, mt: '1px' }} />
          <Typography variant="body2" sx={{ fontSize: '11.5px' }}>
            Copy a field and paste it into the document where the signer should complete it. Add a
            recipient for each person who needs to sign.
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ px: 2 }}>
        {Array.from({ length: count }, (_, i) => i + 1).map((recipient) => (
          <Accordion
            key={recipient}
            expanded={expanded === recipient}
            onChange={() => setExpanded(recipient)}
            disableGutters
            sx={{
              mb: 1,
              border: `1px solid ${portant.parchment}`,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 44 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '12.5px', fontWeight: 600 }}>
                Recipient {recipient}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Stack divider={<Box sx={{ borderBottom: `1px solid ${portant.notebook}` }} />}>
                {FIELDS.map((field) => {
                  const stdKey = copyKey(recipient, field.key, false);
                  const reqKey = copyKey(recipient, field.key, true);
                  return (
                    <Stack
                      key={field.key}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ py: 1 }}
                    >
                      <Box sx={{ color: portant.toasted, display: 'flex' }}>{field.icon}</Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '12.5px', fontWeight: 600 }}>{field.label}</Typography>
                        <Typography sx={{ fontSize: '10.5px', color: portant.charred }}>{field.hint}</Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        justifyContent="flex-end"
                        useFlexGap
                        sx={{ rowGap: 0.5 }}
                      >
                        <Button
                          size="small"
                          variant={copiedKey === stdKey ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleCopy(recipient, field, false)}
                          sx={{ fontSize: '11px', height: '1.875rem', px: 1.25, minWidth: 64 }}
                        >
                          {copiedKey === stdKey ? 'Copied' : 'Copy'}
                        </Button>
                        {field.supportsRequired && (
                          <Button
                            size="small"
                            variant={copiedKey === reqKey ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => handleCopy(recipient, field, true)}
                            sx={{ fontSize: '11px', height: '1.875rem', px: 1.25 }}
                          >
                            {copiedKey === reqKey ? 'Copied' : 'Copy required'}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            variant="outlined"
            onClick={addRecipient}
            disabled={count >= MAX_RECIPIENTS}
            sx={{ fontSize: '11px', textTransform: 'none' }}
          >
            Add recipient
          </Button>
          {count > 1 && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={removeLastRecipient}
              sx={{ fontSize: '11px', textTransform: 'none', color: portant.charred }}
            >
              Remove last
            </Button>
          )}
        </Stack>
        {count >= MAX_RECIPIENTS && (
          <Typography sx={{ fontSize: '10px', color: portant.charred, mt: 0.75 }}>
            Up to {MAX_RECIPIENTS} recipients are supported.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SignatureFields;
